<template>
    <div class="card" v-show="registration">

        <div class="card-header">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ registration.cohort.name }}</h3>
                </div>
                <div class="col-6 text-right" v-if="$permissions.hasRight('dashboard.learners.write')">
                    <base-button type="secondary" size="sm" @click="ctrl.create()">
                        <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                        <span class="btn-inner--text">{{ $t('groups.new') }}</span>
                    </base-button>
                </div>
            </div>
        </div>

        <p class="card-body pt-3 mb-0 text-sm" v-if="empty">{{ $t('groups.no-group') }}</p>

        <div v-if="!empty">
            
            <el-table class="table-responsive table-flush"
                    :show-header="false"
                    :data="rows"
                    ref="groups">

                <el-table-column type="expand" class-name="expand">
                    <template v-slot="{row}">
                        <div class="avatar-group">
                            <span class="avatar rounded-circle" v-for="(member, index) in row.members" :key="index">
                                <a href="#" @click.prevent="openOrEditLearner(member)">
                                    <img :src="member.avatar">
                                </a>
                            </span>
                        </div>
                        <div>
                            <small class="description">{{ row.description }}</small>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column>
                    <template v-slot="{row}">
                        <h4 class="mb-0">
                            <a href="#" @click.prevent="toggleGroup(row)">
                                {{ row.name }}
                            </a>
                        </h4>
                    </template>
                </el-table-column>

                <el-table-column align="right" width="100">
                    <template v-slot="{row}">
                        <div class="m-1">
                            <base-button type="primary" size="sm" @click="openGroup(row)">
                                {{ $t('dashboard.follow-up') }}
                            </base-button>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column align="right" class-name="actions"
                    :width="$permissions.hasRight('dashboard.learners.write') ? 30 : 20"
                >
                    <template v-slot="{row}">
                        <div class="table-actions" v-if="$permissions.hasRight('dashboard.learners.write')">
                            <actions-dropdown :actions="actions(row)" :large="true"></actions-dropdown>
                        </div>
                    </template>
                </el-table-column>
                
            </el-table>
        </div>

        <portal to="modals">

            <form-modal v-model="ctrl.modals.edit"
                :title="$t('common.group')"
                :loading="ctrl.form.loading"
                :centered="false"
            >
                <group-form v-model="ctrl.form.data"
                    :errors="ctrl.form.errors"
                    :modal="ctrl.modals.edit"
                    :learners="learners"
                >
                </group-form>
            </form-modal>

            <confirm-modal v-model="ctrl.modals.confirmDelete">
                <p>{{ $t('groups.delete-confirm') }}</p>
            </confirm-modal>

        </portal>

    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import GroupForm from './GroupForm'

    export default {

        components: {
            GroupForm
        },

        props: {

            registration: {
                type: Object,
                required: true
            },

            learners: {
                type: Array,
                required: true
            },

            groups: {
                type: Array,
                required: true
            }
        },

        data() {
            return {
                loaded: false,
                ctrl: new CrudController({
                    endpoint: '/astral/api/front/groups',
                    mapData: this.mapData,
                    unmapData: this.unmapData,
                    query: {
                        sort: ['name'],
                        accessors: ['members'],
                        filters: { registration_id: this.registration.id }
                    }
                }),
            }
        },

        computed: {
            
            empty() {
                return this.rows.length == 0
            },

            rows() {
                if (!this.loaded) {
                    return this.groups
                }
                return this.ctrl.rows
            }
        },

        created() {
            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
            })
        },

        methods: {
            
            actions(group) {
                return [
                    {
                        label: this.$t('common.edit'),
                        handler: id => {
                            this.ctrl.update(id)
                        },
                        data: group.id
                    },
                    {
                        label: this.$t('common.delete'),
                        handler: id => {
                            this.ctrl.delete(id)
                        },
                        data: group.id
                    }
                ]
            },

            mapData(group) {
                return {
                    id: group ? group.id : null,
                    name: group ? group.name : null,
                    description: group ? group.description : null,
                    members: group ? group.members : null,
                    members_selection: group ? group.members.map(member => member.id) : [],
                    registration_id: this.registration.id
                }
            },

            unmapData(data) {
                return data
            },

            toggleGroup(group) {
                this.$refs.groups.toggleRowExpansion(group)
            },

            openGroup(group) {
                if (this.$route.params.simulationId) {
                    this.$router.push({
                        name: 'dashboard-group-simulation',
                        params: {
                            groupId: group.id,
                            registrationId: this.$route.params.registrationId,
                            simulationId: this.$route.params.simulationId,
                        }
                    });
                } else {
                    this.$router.push({
                        name: 'dashboard-group-registration',
                        params: {
                            groupId: group.id,
                            registrationId: this.$route.params.registrationId,
                        }
                    });
                }
            },

            openOrEditLearner(learner) {
                if (this.$route.params.simulationId) {
                    const learnerWithState = this.learners.find(one => one.id == learner.id)
                    if (learnerWithState.state.published) {
                        this.openLearnerSimulation(learner)
                    } else {
                        this.editLearnerSimulation(learner)
                    }
                } else {
                    this.openLearnerRegistration(learner)
                }
            },

            openLearnerRegistration(learner) {
                this.$router.push({ name: 'dashboard-learner-registration',
                    params: {
                        learnerId: learner.id,
                        registrationId: this.registration.id
                    }
                })
            },

            openLearnerSimulation(learner) {
                this.$router.push({ name: 'dashboard-learner-simulation',
                    params: { 
                        learnerId: learner.id,
                        registrationId: this.registration.id,
                        simulationId: this.$route.params.simulationId,
                    }
                })
            },

            editLearnerSimulation(learner) {
                this.$router.push({ name: 'dashboard-learner-simulation-edit',
                    params: { 
                        learnerId: learner.id,
                        registrationId: this.registration.id,
                        simulationId: this.$route.params.simulationId,
                    }
                })
            }
        }
    }
</script>

<style scoped>

    .description {
        white-space: pre-wrap;
    }
    .card >>> td {
        padding-right: 0;
        padding-left: 0;
    }
    .card >>> td.name {
        padding-left: 0;
    }
    .card >>> td.actions {
        padding-right: 0.5rem;
    }
    .card >>> td.el-table__expanded-cell {
        padding-right: 1.5rem;
        padding-left: 1.5rem;
        padding-top: 0.5rem;
    }
    
</style>